import React, { useState } from "react";
import useBoardAndItems from "../hooks/context/useBoardAndItems";
import ItemsTable from "../components/Resynchronization/ItemsTable";
import useBoardView from "../hooks/api/useBoardView";
import useUser from "../hooks/context/userHook";
import useTokenHook from "../hooks/context/useTokenHook";
import { Text, Button, Dropdown, Tab, TabList, TabPanel, TabPanels, TabsContext } from "@vibe/core";
import CustomPagination from "../components/Pagination";
import ErrorPage from "../components/errorPage";
import PageLoader from "../components/PageLoader";
import { useCheckUserOnboarding } from "../hooks/api/useCheckUserOnboarding";
import EmptyStateBoardView from "@components/Resynchronization/EmptyState/EmptyStateBoardView";
import useRevalidateOnAddItemCompleted from "../hooks/ui/useRevalidateOnAddItemCompleted";
import useFirstResolveLatch from "../hooks/ui/useFirstResolveLatch";
import { AutomationBanner } from "@components/AutomationBanner/AutomationBanner";
import { ActionHeader } from "@components/Resynchronization/ActionHeader";
import { shouldShowBoardEmptyState } from "@components/Resynchronization/boardViewState";
import SubitemsTable from "@components/Resynchronization/SubitemsTable";
import BulkScanSummary from "@components/Resynchronization/BulkScanSummary";
import useSubitemBulkScan from "../hooks/api/useSubitemBulkScan";
import useSubitemDiscrepancyWalk from "../hooks/api/useSubitemDiscrepancyWalk";

const ALL_ITEMS_FILTER = "all";
const DISCREPANCIES_FILTER = "discrepancies";

const options = [
  {
    value: ALL_ITEMS_FILTER,
    label: "All Items",
  },
  {
    value: DISCREPANCIES_FILTER,
    label: "Discrepancies",
  },
];

const DEPRECATION_DATE_LABEL = "January 1, 2027";

/**
 * The dated retirement notice for this page. Not dismissible: it states a date the page stops
 * being available, and a notice someone closed in October is one they have forgotten by then.
 */
const DeprecationNotice = () => (
  <div
    role="status"
    className="px-6 py-3 mt-3 border border-[#FFCB00] bg-[#FFF8E1] rounded-xl text-sm text-[#323338]"
  >
    <span className="font-semibold">Deprecation notice:</span> this page will be deprecated on{" "}
    {DEPRECATION_DATE_LABEL}.
  </div>
);

/**
 * The All Items / Discrepancies control, rendered once per tab and bound to that tab's own
 * state. The two tabs answer different questions — "which synced items drift?" against "which
 * subitems drift?" — and someone narrowing one has no reason to have narrowed the other.
 *
 * CONTROLLED, not `defaultValue`. Vibe's TabPanels renders only the active tab's child, so this
 * unmounts and remounts on every tab switch; an uncontrolled dropdown would come back showing
 * "All Items" while the filter it had set was still in force, which is the same class of lie as
 * a row claiming to be in sync when it isn't.
 */
const FilterDropdown = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) => (
  <div className="mb-2 w-80">
    <Dropdown
      placeholder="Filter"
      options={options}
      className="z-50 dropdown-stories-styles_with-chips"
      onChange={(selectedOption) => onChange(selectedOption.value)}
      value={options.find((option) => option.value === value)}
      size="small"
      clearable={false}
    />
  </div>
);

const Resynchronization = () => {
  const { currentBoard } = useBoardAndItems();
  const { userId } = useUser();
  const { token } = useTokenHook();
  const [retryLoading, setRetryLoading] = useState(false);
  const [page, setPage] = useState(1);
  /**
   * Rows per page, for BOTH tabs' parent lists.
   *
   * Was 8, which is roughly one screen — so a board with a few hundred synced items took tens
   * of page clicks to walk. 25 is the same page size the sync wizard already uses
   * (`useSyncWizardController`'s ITEMS_PER_PAGE), so every parent list in the product agrees
   * rather than each picking a number.
   *
   * One `limit` for both tabs because they page the SAME fetched list: `useBoardView` asks for
   * this many parents, and both tables render from that answer. (The Subitems tab used to have
   * no page control at all — see its own note below.)
   *
   * Not free: this is the `limit` sent to `getSharedItemsBoardView`, so a page is a bigger
   * request and, in Discrepancies mode, a bigger client-side slice of the accumulated set.
   * Neither is capped anywhere — checked both the request builder and the backend route.
   */
  const [limit] = useState(25);
  /*
   * One filter per tab, deliberately not shared.
   *
   * `itemsFilter` drives useBoardView, which switches fetching strategy on it entirely
   * (streamed discrepancy jobs against server-side pagination), so it drives the Items request,
   * its progress bar and its page count.
   *
   * `subitemsFilter` drives the Subitems tab's own board-wide walk (`subitemWalk` below). In
   * Discrepancies mode that tab pages the walk's drifting set and depends on `itemsFilter` not
   * at all; in All mode it still shows the page of parents useBoardView fetched, so that one
   * coupling — which parents are in hand — survives there and only there.
   */
  const [itemsFilter, setItemsFilter] = useState(ALL_ITEMS_FILTER);
  const [subitemsFilter, setSubitemsFilter] = useState(ALL_ITEMS_FILTER);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dropdownDisable, setDropdownDisable] = useState(true);
  const [filteredItemsTotal, setFilteredItemsTotal] = useState(0);
  // Shared Items / Subitems tab selection. Not persisted (never survives a remount) —
  // same rule as discrepancy state itself: ephemeral only. Declared here, with the
  // other hooks, so it holds the same position in hook order on every render even
  // though it's only read in the final return, below the early-return gates.
  const [activeTabId, setActiveTabId] = useState(0);

  const { isAuthorized, error: errorAuth } = useCheckUserOnboarding(userId);

  const {
    isLoading,
    error,
    syncedItems,
    syncedBoardItemIds,
    discrepancies,
    total,
    mutate,
    progressBar,
    isProcessing = false,
    isValidating,
    reset,
    applyOptimisticUpdate,
    fetchSyncItemValues,
  } = useBoardView(currentBoard!, token!, userId!, page, limit, itemsFilter);
  const isContextReady = Boolean(currentBoard && token && userId);

  /*
   * The board-wide scan's job machine, owned by the PAGE and handed to the copy
   * of BulkScanSummary in each tab.
   *
   * Hoisted rather than left inside the panel because the panel now renders
   * twice. TabPanels mounts only the active tab's child, so two independent
   * hooks would mean two independent `jobId`s in component state: a scan
   * started on one tab would be dropped on unmount the moment the user looked
   * at the other, which would come back on the idle strip while that job was
   * still running. The lock behind it is per ACCOUNT, so the strip's "Scan
   * board" button would then answer `busy` and the summary would be reachable
   * from neither tab. One instance here outlives every tab switch.
   *
   * Above the early returns, with the other hooks, so hook order is identical
   * on every render even when the loading and empty-state gates below fire.
   * Costs nothing until used: the hook issues no request until `start()` sets a
   * job id.
   */
  const bulkScan = useSubitemBulkScan({ currentBoard: currentBoard ?? null, token: token ?? null });

  /*
   * The Subitems tab's Discrepancies filter, answered board-wide: every parent listed, scanned a
   * window at a time, and the drifting ones paged. Owned by the page for the same reason
   * `bulkScan` is (the tab unmounts on every switch and the walk must outlive that), and enabled
   * only while that filter is chosen, so a board whose Subitems tab is never narrowed never pays
   * for it. Its window is this page's own `limit` so the two scan bounds cannot drift apart.
   */
  const subitemWalk = useSubitemDiscrepancyWalk({
    currentBoard: currentBoard ?? null,
    token: token ?? null,
    enabled: subitemsFilter === DISCREPANCIES_FILTER,
    windowSize: limit,
  });

  const startIndex = (page - 1) * limit;

  /*
   * The Subitems tab's page count, and the page it is actually on.
   *
   * In Discrepancies mode the set being paged is the walk's drifting set, which grows while the
   * walk runs and shrinks when a resync clears a parent. The shared `page` can therefore point
   * past its end — page three of a set that just became two pages — so the tab is shown the last
   * page that exists rather than an empty one. Clamped here, for the slice and the control alike,
   * rather than written back into `page`: the Items tab shares that state and its own set did
   * not shrink.
   *
   * In All mode both tabs page the same fetched list, so `total` sizes this control exactly as it
   * sizes the Items tab's — server total for a server page, accumulated length for the
   * accumulated set.
   */
  const subitemsPageCount =
    subitemsFilter === DISCREPANCIES_FILTER
      ? Math.ceil(subitemWalk.matching.length / limit)
      : Math.ceil(total / limit);
  const subitemsPage =
    subitemsFilter === DISCREPANCIES_FILTER
      ? Math.min(page, Math.max(1, subitemsPageCount))
      : page;
  const subitemsStartIndex = (subitemsPage - 1) * limit;

  const handleRetry = async () => {
    setRetryLoading(true);
    // Outside the try: synchronous, cannot reject, and a precondition for the refetch rather
    // than part of it — a refetch into un-reset state would merge fresh rows into the stale set.
    reset();
    try {
      await mutate();
    } catch {
      // Swallowed on purpose. The hook's own onError has already set `error` back, which is
      // what puts this block and its Retry back on screen; re-throwing from a click handler
      // would only add an unhandled rejection with no reader.
    } finally {
      // Without this a still-down backend left the button spinning forever.
      setRetryLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Either filter changes the SET its tab pages — `itemsFilter` changes the fetch, and
  // `subitemsFilter` swaps the Subitems tab between the fetched page list and the walk's
  // drifting set — so either resets the page: page four of one set says nothing about the
  // other. The two tabs still share the one `page` state, deliberately, so a tab switch keeps
  // your place instead of silently jumping back to page one.
  const handleItemsFilterChange = (selectedFilter: string) => {
    setItemsFilter(selectedFilter);
    setPage(1);
  };
  const handleSubitemsFilterChange = (selectedFilter: string) => {
    setSubitemsFilter(selectedFilter);
    setPage(1);
  };

  // The add-item flow runs in the item view's iframe; when it finishes, this list is stale
  // until refetched. Same hook the usage charts use for the same reason.
  useRevalidateOnAddItemCompleted(mutate);

  // "Has this board resolved at least once?" — latched, so a later revalidation keeps the
  // rendered data on screen instead of flashing the full-page loader back. See the hook for
  // the rules. Called before any early return so hook order holds when the auth-error
  // branch below short-circuits.
  const hasResolvedOnce = useFirstResolveLatch({ isContextReady, isLoading, error, currentBoard });

  if (errorAuth && !isAuthorized) {
    return <ErrorPage />;
  }

  // First-load gate: a neutral loader (no skeletons) only until the very first
  // resolution. `!hasResolvedOnce` makes this immune to later revalidation; the
  // `syncedItems.length === 0` guard is a same-render safety net before the latch
  // flips; `!error` hands an errored, dataless load to the error UI; and
  // `!isContextReady` covers the pre-token/userId window where the SWR key is null.
  const isInitialLoading =
    !error && !hasResolvedOnce && syncedItems.length === 0 && (!isContextReady || isLoading);

  if (isInitialLoading) {
    return <PageLoader />;
  }

  if (
    shouldShowBoardEmptyState({
      filterQuery: itemsFilter,
      isContextReady,
      isLoading,
      isValidating,
      error,
      itemCount: syncedItems.length,
    })
  ) {
    // The empty state used to own the full viewport height itself. The column does now, so the
    // notice takes its row at the top and the empty state centres in what is left rather than
    // the pair overflowing the iframe by the notice's height.
    return (
      <div className="flex flex-col h-screen">
        <div className="px-12">
          <div className="max-w-[1200px] mx-auto">
            <DeprecationNotice />
          </div>
        </div>
        <EmptyStateBoardView />
      </div>
    );
  }

  return (
    <div>
      <ActionHeader
        syncedItems={syncedItems}
        currentBoard={currentBoard}
        excludedItemIds={syncedBoardItemIds}
        isLoading={isLoading}
      />

      <div className="px-12">
        <div className="pt-0 py-8 max-w-[1200px] mx-auto">
          <DeprecationNotice />

          <AutomationBanner />

          <TabsContext activeTabId={activeTabId}>
            <TabList>
              <Tab onClick={() => setActiveTabId(0)}>Items</Tab>
              <Tab onClick={() => setActiveTabId(1)}>Subitems</Tab>
            </TabList>
            {/*
              Both TabPanel children stay unconditional — TabPanels matches
              activeTabId against a child's index via React.Children.map, so a
              `{cond && <TabPanel/>}` here would silently misalign every panel
              after it. TabPanels itself only renders whichever child's index
              equals activeTabId, so the Subitems panel (and SubitemsTable inside
              it) never mounts — and so never fires a scan — until that tab is
              opened. That's Vibe's own behaviour, not something asserted here.
            */}
            <TabPanels activeTabId={activeTabId}>
              <TabPanel className="!mt-4">
                {/* <div className="flex flex-col gap-2 mb-5">
                  <h2 className="!font-figtree font-bold">Shared Items</h2>
                  <p className="text-sm !font-figtree">
                    View your shared items on this board and the other items it is linked to.
                  </p>
                </div> */}

                <FilterDropdown value={itemsFilter} onChange={handleItemsFilterChange} />

                {/*
                  The board-wide scan, in BOTH tabs rather than the Subitems tab
                  alone.

                  It was subitem-only when it shipped, and so was the scan
                  behind it. It no longer is: one run now reports and writes a
                  parent ITEM's own drifting columns alongside its subitems'
                  (integration-ms `f6566c1`/`fc57aa0`), so the Items tab has a
                  board-wide scan of its own to offer and confirming from either
                  tab writes both sides. Offering it from the Subitems tab only
                  meant the drift this tab exists to show had a board-wide fix
                  the tab never mentioned.

                  Still not in ActionHeader, though ActionHeader is shared by
                  both tabs and that alone would now be defensible. Two reasons
                  hold it here: the panel is not a header control but a surface
                  that expands in place through scanning, the summary, the
                  partial-scan notice and the write report; and it belongs
                  directly beneath a filter, because its "whole board" copy is
                  written to resolve exactly that ambiguity in reading order.

                  Same placement in both panels — below the filter, above the
                  table — so the control does not move when the tab does.
                */}
                <BulkScanSummary currentBoard={currentBoard} token={token} controller={bulkScan} />

                {error ? (
                  <div className="flex flex-col gap-2 justify-center items-center p-2">
                    <Text align="center" element="p" type="text1">
                      An error occurred while retrieving your items. Please try again.
                    </Text>
                    <Button onClick={handleRetry} color="primary" loading={retryLoading}>
                      Retry
                    </Button>
                  </div>
                ) : (
                  <>
                    {itemsFilter === DISCREPANCIES_FILTER && progressBar}

                    <ItemsTable
                      syncedItems={syncedItems}
                      filterQuery={itemsFilter}
                      discrepancies={discrepancies}
                      enableDropdown={setDropdownDisable}
                      handleFilteredTotal={setFilteredItemsTotal}
                      startIndex={startIndex}
                      limit={limit}
                      itemsLoading={isLoading}
                      isProcessing={isProcessing}
                      isValidating={isValidating}
                      applyOptimisticUpdate={applyOptimisticUpdate}
                      fetchSyncItemValues={fetchSyncItemValues}
                      currentBoard={currentBoard}
                    />

                    <div className="flex justify-center items-center mx-auto mt-4 w-full bg-white">
                      <CustomPagination
                        pageCount={
                          itemsFilter === DISCREPANCIES_FILTER
                            ? Math.ceil(filteredItemsTotal / limit)
                            : Math.ceil(total / limit)
                        }
                        currentPage={page}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </>
                )}
              </TabPanel>
              <TabPanel className="!mt-4">
                <FilterDropdown value={subitemsFilter} onChange={handleSubitemsFilterChange} />

                {/*
                  The same panel the Items tab renders, on the same hoisted
                  controller — see the Items tab's copy for why it is in both
                  panels rather than in ActionHeader, and `bulkScan` above for
                  why the two mounts share one job machine.

                  Below the filter, not above it, and that order matters: the
                  panel's own copy says "the whole board" precisely because it
                  sits under a filter that says "Discrepancies only", and a
                  reader resolves that ambiguity in reading order. The scan
                  ignores the filter and the grid's paging entirely.
                */}
                <BulkScanSummary currentBoard={currentBoard} token={token} controller={bulkScan} />

                <SubitemsTable
                  syncedItems={syncedItems}
                  currentBoard={currentBoard}
                  token={token}
                  filterQuery={subitemsFilter}
                  itemsLoading={isLoading}
                  startIndex={subitemsStartIndex}
                  limit={limit}
                  // The ITEMS filter, not `subitemsFilter`: this is the one useBoardView
                  // switches fetching strategy on, so it is what says whether `syncedItems`
                  // above is one server page or the whole accumulated set. See the slice in
                  // SubitemsTable.
                  fetchFilter={itemsFilter}
                  walk={subitemWalk}
                />

                {/*
                  The same control the Items tab has had all along. Without it this tab
                  rendered page one's parents and nothing else — in All mode the rest of the
                  board was simply unreachable from here, and in Discrepancies mode it drew
                  the entire accumulated set at once while the tab beside it showed 25.

                  Sized from this tab's OWN set — see `subitemsPageCount` — never from the Items
                  tab's filtered count: the two tabs filter independently.
                */}
                <div className="flex justify-center items-center mx-auto mt-4 w-full bg-white">
                  <CustomPagination
                    pageCount={subitemsPageCount}
                    currentPage={subitemsPage}
                    onPageChange={handlePageChange}
                  />
                </div>
              </TabPanel>
            </TabPanels>
          </TabsContext>
        </div>
      </div>
    </div>
  );
};

export default Resynchronization;
